import React, { useState, useContext, useMemo } from 'react';
import _ from 'lodash';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faTrashAlt, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { createClientPortfolio, listClientPortfolios, deleteClientPortfolio } from 'services/client-portfolio';
import { listModelPortfolios } from 'services/model-portfolio';
import PortfolioSaveModal from './PortfolioSaveModal';
import PortfolioDeleteConfirmModal from './PortfolioDeleteConfirmModal';
import { AdminContext } from 'layouts/Admin';

function PortfolioActions({
  name = '',
  confirm,
  assets = [],
  databases = [],
  weights = [],
  onSaveSelected = _.noop,
  onLoadSelected = _.noop,
  onClearSelected = _.noop,
}) {
  const context = useContext(AdminContext);
  const [savedPortfolios, setSavedPortfolios] = useState([]);
  const [modelPortfolios, setModelPortfolios] = useState([]);
  const [savedPortfolio, setSavedPortfolio] = useState(null);
  const [savedPortfolioIdToDelete, setSavedPortfolioIdToDelete] = useState(null);
  const [modelPortfolio, setModelPortfolio] = useState(null);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showLoadDropdown1, setShowLoadDropdown1] = useState(false);
  const [showLoadDropdown2, setShowLoadDropdown2] = useState(false);
  const [showPortfolioSaveModal, setShowPortfolioSaveModal] = useState(false);
  const [showPortfolioDeleteConfirmModal, setShowPortfolioDeleteConfirmModal] = useState(false);
  const maxItems = context?.session?.permissions['pa-build-max-saved'] ?? 1;

  const onSaveDropdownToggle = async () => {
    if (showSaveDropdown) {
      setShowSaveDropdown(false);
    } else {
      const [data, err] = await listClientPortfolios();
      setSavedPortfolios(data);
      setShowSaveDropdown(true);
    }

    setShowLoadDropdown1(false);
  };

  const onLoadDropdownToggle1 = async () => {
    if (showLoadDropdown1) {
      setShowLoadDropdown1(false);
    } else {
      const [data, err] = await listClientPortfolios();
      setSavedPortfolios(_.orderBy(data, ['name', ['asc']]));
      setShowLoadDropdown1(true);
    }

    setShowSaveDropdown(false);
  };

  const onLoadDropdownToggle2 = async () => {
    if (showLoadDropdown2) {
      setShowLoadDropdown2(false);
    } else {
      const [data, err] = await listModelPortfolios();
      setModelPortfolios(data);
      setShowLoadDropdown2(true);
    }

    setShowSaveDropdown(false);
  };

  const [data, valid, invalidReason] = useMemo(() => {
    const result = [];
    let sum = 0;
    _.each(weights, (weight, i) => {
      if (isNaN(weight)) return;
      if (assets[i] && weight > 0) {
        result.push({ asset: assets[i], database: databases[i], weight });
        sum += weight;
      }
    });

    const sum100 = Number(sum).toFixed(2) === '100.00';

    const includeSavedPortfolios = result.some((v) => v.database === 'Saved Portfolios');
    const includeModelPortfolios = result.some((v) => v.database === 'Model Portfolios');

    const valid = sum100 && !includeSavedPortfolios && !includeModelPortfolios;

    let invalidReason = '';
    if (!valid) {
      if (result.length === 0) {
        invalidReason = 'Please select at least one asset.';
      } else if (includeSavedPortfolios) {
        invalidReason = 'Saving a "Saved Portfolio" is not allowed.';
      } else if (includeModelPortfolios) {
        invalidReason = 'Saving a "Model Portfolio" is not allowed.';
      } else if (!sum100) {
        invalidReason = 'Total weights must add up to 100%.';
      }
    }

    return [result, valid, invalidReason];
  }, [assets, databases, weights]);

  return (
    <div className="">
      <Dropdown className="tw-mb-1" isOpen={showLoadDropdown2} toggle={onLoadDropdownToggle2}>
        <DropdownToggle nav className="tw-text-xs tw-p-0">
          Model Portfolios
        </DropdownToggle>
        <DropdownMenu right className="dropdown-success tw-max-h-[300px] tw-overflow-y-scroll">
          {modelPortfolios.map((portfolio) => (
            <DropdownItem
              key={portfolio.name}
              onClick={() => {
                setModelPortfolio(portfolio);
                onLoadSelected(portfolio);
              }}
            >
              <div className="tw-flex tw-justify-between">
                <div className="tw-pr-2">
                  <span className="">{portfolio.name}</span>
                </div>
              </div>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
      <Dropdown className="tw-mb-1" isOpen={showLoadDropdown1} toggle={onLoadDropdownToggle1}>
        <DropdownToggle nav className="tw-text-xs tw-p-0">
          Saved Portfolios
        </DropdownToggle>
        <DropdownMenu right className="dropdown-success tw-max-h-[300px] tw-overflow-y-scroll">
          {savedPortfolios.map((portfolio) => (
            <DropdownItem
              key={portfolio.name}
              onClick={() => {
                setSavedPortfolio(portfolio);
                onLoadSelected(portfolio);
              }}
            >
              <div className="tw-flex tw-justify-between">
                <div className="tw-pr-2">
                  <span className="">{portfolio.name}</span>
                </div>
                <div
                  className="tw-h-full hover:tw-text-red-700 hover:tw-text-bold"
                  onClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowPortfolioDeleteConfirmModal(true);
                    setSavedPortfolioIdToDelete(portfolio.id);
                  }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </div>
              </div>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
      <Dropdown className="tw-mb-1" isOpen={showSaveDropdown} toggle={valid ? onSaveDropdownToggle : _.noop}>
        <DropdownToggle
          nav
          className={classnames('tw-text-xs tw-p-0 tw-mr-3', {
            'tw-text-slate-300 hover:tw-text-slate-300 tw-cursor-not-allowed': !valid,
          })}
        >
          <span id={`invalid-${name}`}>Save</span>
          {invalidReason && (
            <UncontrolledTooltip delay={0} target={`invalid-${name}`}>
              {invalidReason}
            </UncontrolledTooltip>
          )}
        </DropdownToggle>
        <DropdownMenu right className="dropdown-success">
          {savedPortfolios.length < maxItems ? (
            <DropdownItem onClick={() => setShowPortfolioSaveModal(true)}>
              <FontAwesomeIcon icon={faCloudArrowUp} className="tw-mr-1" />
              Save as New
            </DropdownItem>
          ) : (
            !savedPortfolio && (
              <DropdownItem>
                <span className="tw-italic tw-text-sm">
                  You have reached the maximum limit of {maxItems} saved portfolios. Please delete some of your existing
                  saved portfolios in order to make room.
                </span>
              </DropdownItem>
            )
          )}
          {savedPortfolio && (
            <DropdownItem
              onClick={() => {
                if (savedPortfolio) {
                  confirm({
                    content: `This will overwrite the existing portfolio "${savedPortfolio.name}". Are you sure you would like to continue with this overwrite?`,
                    callback: (result) => {
                      if (result != 'confirmed') return;
                      createClientPortfolio(savedPortfolio.name, data);
                    },
                  });
                }
              }}
            >
              <FontAwesomeIcon icon={faCloudArrowUp} className="tw-mr-1" />
              Save Current
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
      <Dropdown
        className=""
        isOpen={false}
        toggle={() => {
          confirm({
            content: 'Are you sure to clear all weights?',
            callback: (result) => {
              if (result != 'confirmed') return;
              setSavedPortfolio(null);
              onClearSelected();
            },
          });
        }}
      >
        <DropdownToggle nav className="tw-text-xs tw-p-0">
          Clear
        </DropdownToggle>
      </Dropdown>
      <PortfolioSaveModal
        isOpen={showPortfolioSaveModal}
        onClose={(portfolio) => {
          if (portfolio) {
            onSaveSelected(portfolio);
            setSavedPortfolio(portfolio);
          }
          setShowPortfolioSaveModal(false);
        }}
        data={data}
        portfolios={savedPortfolios}
        confirm={confirm}
      />
      <PortfolioDeleteConfirmModal
        isOpen={showPortfolioDeleteConfirmModal}
        onClose={async (result) => {
          setShowPortfolioDeleteConfirmModal(false);

          if (result !== 'confirmed') return;
          if (!savedPortfolioIdToDelete) return;

          await deleteClientPortfolio(savedPortfolioIdToDelete);
          setShowLoadDropdown1(false);
        }}
      />
    </div>
  );
}

export default PortfolioActions;
